/**
 * The shared validation thread — one durable, attributed thread per loan
 * (`GET /experience/chat/{dealId}`), with a loan-grounded assistant turn on
 * every message (`POST` → text/event-stream, AG-UI frames).
 *
 * Only mounted when `/check` says `experienceChatEnabled` (the "AI Review
 * Visibility" org group); a 403 mid-session hides the panel rather than
 * toasting. Other people's messages arrive out of band, so the thread is
 * re-read every ~15 s while the panel is open and merged by `at`. The user's
 * message is persisted BEFORE the model streams — on a dropped stream we
 * re-GET the thread rather than re-send.
 *
 * Frames: RUN_STARTED · CUSTOM{experience_context} (the grounding chip) ·
 * TEXT_MESSAGE_START/CONTENT/END (the streamed reply) ·
 * CUSTOM{model_not_configured} (persistent banner; the reply is then the
 * deterministic summary) · RUN_ERROR · RUN_FINISHED.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { experienceApi } from "../../../api/experienceApi";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import ExperienceChatComposer from "./ExperienceChatComposer";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { slate, status, teal } from "../../../theme/colors";

const POLL_MS = 15000;
const keyOf = (m) => `${m?.role}|${m?.at}|${m?.user?.email || ""}`;

const mergeByAt = (current, incoming) => {
  const seen = new Set(current.map(keyOf));
  const merged = [...current];
  (incoming || []).forEach((m) => {
    if (!seen.has(keyOf(m))) merged.push(m);
  });
  return merged.sort((a, b) =>
    String(a?.at || "").localeCompare(String(b?.at || "")),
  );
};

const Bubble = ({ m, streaming, docsById, onOpenDoc }) => {
  const isUser = m?.role === "user";
  const starter =
    m?.role === "assistant" && !m?.user && !m?.inReplyTo && !streaming;
  const attachments = Array.isArray(m?.attachments) ? m.attachments : [];
  const images = Array.isArray(m?.images) ? m.images : [];
  if (starter)
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: "8px",
          bgcolor: slate[50],
          border: `1px dashed ${slate[200]}`,
        }}
      >
        <Txt sx={{ fontSize: 12, color: slate[600], whiteSpace: "pre-wrap" }}>
          {m.content}
        </Txt>
      </Box>
    );
  return (
    <Flx
      column
      g={0.25}
      sx={{ alignItems: isUser ? "flex-end" : "flex-start" }}
    >
      <Txt sx={{ fontSize: 10.5, color: slate[500] }}>
        {isUser
          ? m?.user?.name || m?.user?.email || "You"
          : `Assistant · answered ${m?.inReplyTo || "the thread"}`}
        {m?.at ? ` · ${formatDateTime(m.at, "time") || ""}` : ""}
      </Txt>
      <Box
        sx={{
          maxWidth: "88%",
          px: 1.5,
          py: 1,
          borderRadius: "10px",
          bgcolor: isUser ? teal[600] : "#fff",
          color: isUser ? "#fff" : slate[900],
          border: isUser ? "none" : `1px solid ${slate[200]}`,
        }}
      >
        {m?.content ? (
          <Txt sx={{ fontSize: 12.5, whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {m.content}
            {streaming ? (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  width: 6,
                  height: 12,
                  ml: 0.5,
                  bgcolor: slate[400],
                  animation: "blink 1s steps(2) infinite",
                  "@keyframes blink": { to: { opacity: 0 } },
                }}
              />
            ) : null}
          </Txt>
        ) : null}
        {/* attachments — a displayed file is always previewable */}
        {attachments.length ? (
          <Flx ac g={0.5} wrap sx={{ mt: m?.content ? 0.75 : 0 }}>
            {attachments.map((a) => {
              const doc = docsById?.get(String(a.loanDocumentId));
              return (
                <Chip
                  key={a.loanDocumentId}
                  size="small"
                  icon={
                    <DescriptionOutlined
                      sx={{
                        fontSize: 13,
                        color: isUser ? "#fff !important" : undefined,
                      }}
                    />
                  }
                  label={a.fileName || doc?.file_display_name || "Document"}
                  onClick={doc && onOpenDoc ? () => onOpenDoc(doc) : undefined}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    maxWidth: 220,
                    bgcolor: isUser ? "rgba(255,255,255,.16)" : slate[100],
                    color: isUser ? "#fff" : slate[800],
                    "&:hover": doc
                      ? {
                          bgcolor: isUser
                            ? "rgba(255,255,255,.26)"
                            : slate[200],
                        }
                      : undefined,
                  }}
                />
              );
            })}
          </Flx>
        ) : null}
        {images.length ? (
          <Flx
            ac
            g={0.5}
            wrap
            sx={{ mt: m?.content || attachments.length ? 0.75 : 0 }}
          >
            {images.map((im, i) => (
              <Box
                key={`${im.fileName || "image"}-${i}`}
                component="img"
                src={
                  im.dataUrl ||
                  im.url ||
                  (im.dataBase64
                    ? `data:${im.mimeType || "image/png"};base64,${im.dataBase64}`
                    : undefined)
                }
                alt={im.fileName || "attached image"}
                title={im.fileName || ""}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: "cover",
                  borderRadius: "6px",
                  border: `1px solid ${isUser ? "rgba(255,255,255,.3)" : slate[200]}`,
                  display: "block",
                }}
              />
            ))}
          </Flx>
        ) : null}
      </Box>
    </Flx>
  );
};

Bubble.propTypes = {
  m: PropTypes.object,
  streaming: PropTypes.bool,
  docsById: PropTypes.instanceOf(Map),
  onOpenDoc: PropTypes.func,
};

const ExperienceChatPanel = ({ dealId, onForbidden }) => {
  const dispatch = useDispatch();
  const loanDocumentsRaw = useSelector((s) => s.loanDrilldown?.loanDocuments);
  const loanDocuments = useMemo(
    () => loanDocumentsRaw || [],
    [loanDocumentsRaw],
  );
  const docsById = useMemo(
    () => new Map(loanDocuments.map((d) => [String(d?._id), d])),
    [loanDocuments],
  );
  const [messages, setMessages] = useState([]);
  const [assistant, setAssistant] = useState(null); // GET /chat `assistant` status (booleans only)
  const [streaming, setStreaming] = useState(null); // { messageId, content }
  const [context, setContext] = useState(null); // experience_context frame value
  const [notConfigured, setNotConfigured] = useState(null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  const load = useCallback(() => {
    if (!dealId) return;
    experienceApi
      .chat(dealId)
      .then((d) => {
        setMessages((cur) => mergeByAt(cur, d?.messages || []));
        if (d?.assistant) setAssistant(d.assistant);
      })
      .catch((err) => {
        if (err?.response?.status === 403) onForbidden?.();
      });
  }, [dealId, onForbidden]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, streaming]);

  // `payload` comes from the composer: { message, text, attachments, images }.
  // `text` is what the server reads (message + the attached-document trailer);
  // attachments / images ride along for the day the turn accepts them.
  const send = async (payload) => {
    const text = (payload?.text || "").trim();
    if (!text || streaming) return;
    setError(null);
    // Optimistic user bubble; the server stamps the real attribution and `at`.
    const optimistic = {
      role: "user",
      content: payload.message || text,
      attachments: payload.attachments,
      images: payload.images,
      at: new Date().toISOString(),
      user: null,
      _optimistic: true,
    };
    setMessages((cur) => [...cur, optimistic]);
    setStreaming({ messageId: null, content: "" });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await experienceApi.streamChatTurn(
        dealId,
        {
          message: text,
          attachments: payload.attachments?.length
            ? payload.attachments
            : undefined,
          images: payload.images?.length
            ? payload.images.map(({ fileName, mimeType, dataBase64 }) => ({
                fileName,
                mimeType,
                dataBase64,
              }))
            : undefined,
        },
        {
          signal: controller.signal,
          onFrame: (f) => {
            switch (f?.type) {
              case "CUSTOM":
                if (f.name === "experience_context") setContext(f.value || {});
                if (f.name === "model_not_configured")
                  setNotConfigured(f.value || true);
                break;
              case "TEXT_MESSAGE_START":
                setStreaming({ messageId: f.messageId, content: "" });
                break;
              case "TEXT_MESSAGE_CONTENT":
                setStreaming((s) => ({
                  messageId: f.messageId,
                  content: `${s?.content || ""}${f.delta || ""}`,
                }));
                break;
              case "RUN_ERROR":
                setError(f.message || "The assistant run failed.");
                break;
              default:
                break;
            }
          },
        },
      );
    } catch (err) {
      if (err?.status === 403) {
        onForbidden?.();
        return;
      }
      if (err?.name !== "AbortError")
        setError(err?.message || "The reply could not be streamed.");
    } finally {
      setStreaming(null);
      abortRef.current = null;
      // The user message was persisted before the model streamed — re-read
      // the thread so the attributed copy (and the reply) replace the optimistic one.
      setMessages((cur) => cur.filter((m) => !m._optimistic));
      load();
    }
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  const grounding = useMemo(() => {
    if (!context) return null;
    const bits = [];
    if (context.loanGrounded) bits.push("grounded in the loan");
    if (context.documentCount != null)
      bits.push(`${context.documentCount} docs`);
    if (context.openConditions != null)
      bits.push(`${context.openConditions} open conditions`);
    if (context.openExceptions != null)
      bits.push(`${context.openExceptions} open exceptions`);
    if (context.verified != null && context.items != null)
      bits.push(`${context.verified}/${context.items} verified`);
    return bits.join(" · ");
  }, [context]);

  return (
    <Flx column g={1} sx={{ height: "100%", minHeight: 0 }}>
      {grounding ? (
        <Flx ac g={0.75} wrap>
          <Chip
            size="small"
            label={grounding}
            sx={{
              height: 18,
              fontSize: 10.5,
              bgcolor: teal[100],
              color: teal[700],
            }}
          />
        </Flx>
      ) : null}
      {context?.unavailable?.length ? (
        <Txt sx={{ fontSize: 11.5, color: status.warning.fg }}>
          Not available this turn: {context.unavailable.join(", ")}
        </Txt>
      ) : null}
      {notConfigured ? (
        <Alert severity="warning" sx={{ fontSize: 12 }}>
          No model deployment is configured — replies are the deterministic loan
          summary, not an assistant answer.
        </Alert>
      ) : null}

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 200,
          maxHeight: 420,
          overflowY: "auto",
          px: 1.25,
          py: 1,
          borderRadius: "10px",
          bgcolor: slate[50],
          border: `1px solid ${slate[200]}`,
        }}
      >
        <Flx column g={1.25}>
          {messages.map((m, i) => (
            <Bubble
              key={`${keyOf(m)}-${i}`}
              m={m}
              docsById={docsById}
              onOpenDoc={(d) => dispatch(filePreviewSetData(d))}
            />
          ))}
          {streaming ? (
            <Bubble
              m={{
                role: "assistant",
                content: streaming.content,
                inReplyTo: "you",
              }}
              streaming
            />
          ) : null}
        </Flx>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ fontSize: 12 }}>
          {error}
        </Alert>
      ) : null}

      <ExperienceChatComposer
        docs={loanDocuments}
        acceptsImages={!!assistant?.acceptsImages}
        disabled={!!streaming}
        onSend={send}
      />
    </Flx>
  );
};

ExperienceChatPanel.propTypes = {
  dealId: PropTypes.string,
  onForbidden: PropTypes.func,
};

export default ExperienceChatPanel;
